import React, { useState, useEffect, useRef } from 'react';
import { testBackendConnection } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import Link from 'next/link';
import useTokenManager from '@/hooks/useTokenManager';
import { motion, AnimatePresence } from 'framer-motion';

export const AuthStatusIndicator: React.FC = () => {
  const [authStatus, setAuthStatus] = useState({
    isBackendAvailable: false,
    isAuthenticated: false,
    username: null as string | null,
    firstName: null as string | null,
    lastName: null as string | null,
    email: null as string | null,
    debugInfo: null as string | null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showBackendError, setShowBackendError] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const tokenManager = useTokenManager();

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (isLoading) {
      setShowBackendError(false);
      timer = setTimeout(() => {
        setShowBackendError(true);
      }, 800); // délai avant affichage de l'erreur
    } else {
      setShowBackendError(false);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isLoading]);

  const checkAuthStatus = async () => {
    try {
      setIsLoading(true);
      
      // Vérifier la connexion au backend
      const backendAvailable = await testBackendConnection();
      
      // Vérifier l'authentification avec notre hook
      const isAuth = tokenManager.isAuthenticated();
      
      let isAuthenticated = isAuth;
      let username: string | null = null;
      let firstName: string | null = null;
      let lastName: string | null = null;
      let email: string | null = null;
      let debugInfo: string = '';
      
      if (isAuth) {
        // Récupérer le profil utilisateur
        let userProfile = await tokenManager.getUserProfile();
        // essayer de récupérer les informations directement depuis localStorage
        if (!userProfile && isAuthenticated && typeof localStorage !== 'undefined') {
          console.log('🔍 Tentative de récupération du profil depuis localStorage');
          try {
            const storedProfile = localStorage.getItem('sentinel_user_profile');
            if (storedProfile) {
              userProfile = JSON.parse(storedProfile);
              console.log('🔍 Profil récupéré depuis localStorage:', userProfile);
            }
          } catch (e) {
            console.error('🔍 Erreur lors de la récupération depuis localStorage:', e);
          }
        }
        
        if (userProfile) {
          console.log('🔍 Profil utilisateur trouvé:', {
            hasName: !!userProfile.name,
            hasUsername: !!userProfile.username,
            hasEmail: !!userProfile.email
          });
          
          username = userProfile.username || userProfile.email || null;
          firstName = userProfile.first_name || userProfile.name?.split(' ')[0] || null;
          lastName = userProfile.last_name || (userProfile.name?.split(' ').length > 1 ? userProfile.name.split(' ').slice(1).join(' ') : null);
          email = userProfile.email || null;
          
          // Déboguer les tentatives de rafraîchissement du token
          const tokenIsValid = tokenManager.isAuthenticated;
          const hasRefreshToken = !!tokenManager.getRefreshToken();
          
          debugInfo = `Token valide: ${tokenIsValid}, Has refresh: ${hasRefreshToken}`;
        } else if (isAuthenticated) {
          debugInfo = 'Tokens found but no user profile';
          console.log('🔍 Tokens trouvés mais pas de profil utilisateur');
          
          // Si authentifié mais pas de profil, essayer de récupérer depuis le token
          const accessToken = tokenManager.getAccessToken();
          if (accessToken) {
            const decodedToken = await tokenManager.decodeToken(accessToken);
            if (decodedToken) {
              username = decodedToken.username || decodedToken.preferred_username || decodedToken.email || null;
              email = decodedToken.email || null;
              firstName = decodedToken.given_name || null;
              lastName = decodedToken.family_name || null;
            }
          }
        }
      }
      
      console.log('Auth Status Check:', {
        backendAvailable,
        isAuthenticated,
        username,
        firstName,
        lastName,
        email,
        debugInfo
      });

      setAuthStatus({
        isBackendAvailable: backendAvailable,
        isAuthenticated,
        username,
        firstName,
        lastName,
        email,
        debugInfo
      });
    } catch (error) {
      console.error('Error checking auth status:', error);
      setAuthStatus({
        isBackendAvailable: false,
        isAuthenticated: false,
        username: null,
        firstName: null,
        lastName: null,
        email: null,
        debugInfo: `Unexpected error: ${error instanceof Error ? error.message : String(error)}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuthStatus();
    
    // Refresh status every 30 seconds
    const intervalId = setInterval(checkAuthStatus, 30000);
    
    return () => clearInterval(intervalId);
  }, []);

  // Show loading state
  if (isLoading) {
    return <Badge variant="secondary">Vérification...</Badge>;
  }

  if (!authStatus.isBackendAvailable && showBackendError) {
    return (
      <div>
        <Badge variant="destructive">Backend Indisponible</Badge>
        {authStatus.debugInfo && (
          <div className="text-xs text-red-500 mt-1">
            Debug: {authStatus.debugInfo}
          </div>
        )}
      </div>
    );
  }

  if (authStatus.isAuthenticated) {
    return (
      <div className="flex items-center space-x-3">
        <Badge variant="success" className="flex items-center">
          <span className="inline-block h-2 w-2 rounded-full bg-green-400 mr-2"></span>
          Connecté{authStatus.username ? `: ${authStatus.username}` : ''}
        </Badge>
        <motion.div 
          className="relative group"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
        >
          {/* Effet de scintillement lumineux autour du bouton */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 opacity-75 blur-md group-hover:opacity-100 group-hover:blur-lg transition-all duration-300 animate-pulse-slow"></div>
          
          {/* Petit point lumineux vert/violet */}
          <div className="absolute -right-1 -top-1 flex items-center justify-center">
            <div className="relative h-3 w-3">
              <AnimatePresence>
                <motion.div 
                  key="pulse-ring"
                  className="absolute inset-0 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500"
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.6, 0.2, 0.6]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatType: "loop"
                  }}
                />
              </AnimatePresence>
              <div className="absolute inset-0.5 rounded-full bg-cyan-500 shadow-lg">
                <div className="absolute inset-0 rounded-full bg-cyan-200 opacity-50 animate-ping-slow"></div>
              </div>
            </div>
          </div>

          {/* Petites lignes de scanner qui passent sur le bouton */}
          <div className="absolute inset-0 overflow-hidden rounded-full pointer-events-none">
            <div className="scanner-line-h h-0.5 w-full bg-cyan-200/30"></div>
            <div className="scanner-line-v h-full w-0.5 bg-cyan-200/30"></div>
          </div>
          
          {/* Le bouton lui-même */}
          <Link
            href="/dashboard"
            className="relative px-4 py-1.5 bg-gradient-to-r from-blue-500 to-cyan-500 text-white uppercase tracking-wide text-xs font-semibold rounded-full transition-all duration-300 shadow-lg group-hover:shadow-cyan-500/50 focus:outline-none border border-cyan-300/20 flex items-center justify-center z-10 min-w-[140px]"
          >
            <span className="mr-1.5 font-bold">Tableau de bord</span>
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              className="w-3 h-3"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="3" y1="9" x2="21" y2="9"></line>
              <line x1="9" y1="21" x2="9" y2="9"></line>
            </svg>
          </Link>
        </motion.div>
      </div>
    );
  }

  // Pas de code audio, nous utilisons seulement l'effet visuel du point clignotant

  return (
    <motion.div 
      className="relative group"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Effet de scintillement lumineux autour du bouton */}
      <div className="absolute inset-0 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 opacity-75 blur-md group-hover:opacity-100 group-hover:blur-lg transition-all duration-300 animate-pulse-slow"></div>
      
      {/* Petit bip scintillant */}
      <div className="absolute -right-1 -top-1 flex items-center justify-center">
        <div className="relative h-3 w-3">
          <AnimatePresence>
            <motion.div 
              key="pulse-ring"
              className="absolute inset-0 rounded-full bg-cyan-400"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.6, 0.2, 0.6]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          </AnimatePresence>
          <div className="absolute inset-0.5 rounded-full bg-cyan-500 shadow-lg">
            <div className="absolute inset-0 rounded-full bg-cyan-200 opacity-50 animate-ping-slow"></div>
          </div>
        </div>
      </div>

      {/* Petites lignes de scanner qui passent sur le bouton */}
      <div className="absolute inset-0 overflow-hidden rounded-full pointer-events-none">
        <div className="scanner-line-h h-0.5 w-full bg-cyan-200/30"></div>
        <div className="scanner-line-v h-full w-0.5 bg-cyan-200/30"></div>
      </div>
      
      {/* Le bouton lui-même */}
      <Link
        href="/login"
        className="relative px-4 py-1.5 bg-gradient-to-r from-blue-500 to-cyan-500 text-white uppercase tracking-wide text-xs font-semibold rounded-full transition-all duration-300 shadow-lg group-hover:shadow-cyan-500/50 focus:outline-none border border-cyan-300/20 flex items-center justify-center z-10 min-w-[140px]"
      >
        <span className="mr-1.5 font-bold">Connexion</span>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          className="w-3 h-3"
        >
          <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
          <polyline points="10 17 15 12 10 7"/>
          <line x1="15" y1="12" x2="3" y2="12"/>
        </svg>
      </Link>
    </motion.div>
  );
};
